import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Tooltip } from 'antd';
import {
    AlignCenterOutlined,
    AlignLeftOutlined,
    AlignRightOutlined,
    BoldOutlined,
    DownOutlined,
    ItalicOutlined
} from '@ant-design/icons';

import {
    RICH_TEXT_COLOR_PRESETS,
    colorsClose,
    injectDefaultFontSizeIntoRichTextHtml,
    matchPresetValue,
    parseCssColorToRgb,
    pruneRedundantNestedParentFontSizeInRichTextRoot,
    sanitizePastedRichTextHtml,
    sanitizePodRichHtml,
    stripInlineFontSizeFromDescendants
} from './richTextColorUtils';

/** 工具栏可选字号（px），步进 2，最大 40 */
const RICH_TEXT_FONT_SIZE_OPTIONS_PX: readonly number[] = Object.freeze(
    Array.from({ length: (40 - 12) / 2 + 1 }, (_, i) => 12 + i * 2)
);

/** 预置白色：编辑区已有 `text-white` 时 execCommand(foreColor) 常「成功」但不写入内联 color，JSON 会丢白色 */
const PRESET_WHITE_HEX = RICH_TEXT_COLOR_PRESETS[0].value;
/** 与 POD 配置页内置 HTML 一致：橘 16px、白字 22px（数字 24 需在工具栏另选字号或由模板 span 写入） */
const POD_OVERLAY_EDITOR_ORANGE_PX = 16;
const POD_OVERLAY_EDITOR_WHITE_TEXT_PX = 22;
const PRESET_ORANGE_HEX = RICH_TEXT_COLOR_PRESETS[1]?.value ?? '#fa8c16';
const isPresetWhiteColor = (c: string) => colorsClose(c, PRESET_WHITE_HEX, 12);
const isPresetOrangeColor = (c: string) => colorsClose(c, PRESET_ORANGE_HEX, 12);

/** 下拉框「未选字号」占位 value；避免默认勾选数字且与 HTML select 的 value 必须是 option 之一 兼容 */
const FONT_SIZE_SELECT_PLACEHOLDER = '__size_none__';

export type RichTextColorEditorProps = {
    value: string;
    onChange: (html: string) => void;
    placeholder?: string;
    onFocus?: () => void;
    /** 编辑区底色：'overlay' 深底白字（相机叠层默认）；'light' 白底黑字（白底弹窗用） */
    surface?: 'overlay' | 'light';
};

export const RichTextColorEditor: React.FC<RichTextColorEditorProps> = ({
    value,
    onChange,
    placeholder,
    onFocus,
    surface = 'overlay'
}) => {
    const applyCommand = (command: string) => {
        document.execCommand(command, false);
    };

    const editorRootRef = useRef<HTMLDivElement | null>(null);
    const lastRangeInEditorRef = useRef<Range | null>(null);
    /** 与 applyColorToSelection 同一轮应用，在 rAF 里写入状态（与光标/DOM 无关） */
    const pendingAppliedColorRef = useRef<string | null>(null);
    const [customPickerColor, setCustomPickerColor] = useState<string>('#1677ff');
    const [userSelectedColor, setUserSelectedColor] = useState<string | null>(null);
    const [customSwatchActive, setCustomSwatchActive] = useState(false);
    const [fontSizePx, setFontSizePx] = useState<number | null>(null);
    const lastSyncedValueRef = useRef<string>('');
    /** 用户在工具栏明确选中的颜色：新输入必须使用该色；未设置时由光标处继承（不拦截 beforeinput） */
    const toolbarExplicitTypingColorRef = useRef<string | null>(null);
    /** 用户在工具栏明确选中的字号：新输入必须使用该字号；未点过字号前不强制，继承编辑区橘色基准（POD_OVERLAY_EDITOR_ORANGE_PX） */
    const toolbarExplicitTypingFontSizePxRef = useRef<number | null>(null);

    const rememberSelectionInEditor = () => {
        const root = editorRootRef.current;
        if (!root) return;
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const r = sel.getRangeAt(0);
        if (root.contains(r.commonAncestorContainer)) {
            lastRangeInEditorRef.current = r.cloneRange();
        }
    };

    const getSelectionRange = (): Range | null => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return null;
        return sel.getRangeAt(0);
    };

    const refreshActiveSwatch = useCallback(() => {
        const pending = pendingAppliedColorRef.current;
        if (pending == null) return;
        pendingAppliedColorRef.current = null;
        const preset = matchPresetValue(pending);
        if (preset) {
            setUserSelectedColor(preset);
            setCustomSwatchActive(false);
        } else if (parseCssColorToRgb(pending)) {
            setUserSelectedColor(null);
            setCustomSwatchActive(true);
            setCustomPickerColor(pending);
        }
    }, []);

    const scheduleRefreshActiveSwatch = () => requestAnimationFrame(() => refreshActiveSwatch());

    const insertStyledTextAtSelection = (text: string, style: Partial<CSSStyleDeclaration>) => {
        const root = editorRootRef.current;
        if (!root) return;
        const sel = window.getSelection();
        let range: Range | null = null;
        if (sel && sel.rangeCount > 0) {
            const r = sel.getRangeAt(0);
            if (root.contains(r.commonAncestorContainer)) {
                range = r;
            }
        }
        if (!range && lastRangeInEditorRef.current) {
            const backup = lastRangeInEditorRef.current;
            if (root.contains(backup.commonAncestorContainer)) {
                range = backup.cloneRange();
            }
        }
        if (!range) return;

        const span = document.createElement('span');
        Object.entries(style).forEach(([k, v]) => {
            if (v == null) return;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (span.style as any)[k] = v;
        });
        span.textContent = text;

        try {
            range.deleteContents();
            range.insertNode(span);
        } catch {
            return;
        }

        // 将光标放到插入内容之后
        try {
            const caret = document.createRange();
            caret.setStartAfter(span);
            caret.collapse(true);
            sel?.removeAllRanges();
            sel?.addRange(caret);
            lastRangeInEditorRef.current = caret.cloneRange();
        } catch {
            /* noop */
        }

        if (Object.keys(style).length > 0) {
            pruneRedundantNestedParentFontSizeInRichTextRoot(root);
        }

        const html = root.innerHTML;
        if (lastSyncedValueRef.current === html) return;
        lastSyncedValueRef.current = html;
        onChange(html);
    };

    const handleEditorPaste = useCallback(
        (e: React.ClipboardEvent<HTMLDivElement>) => {
            const editorRoot = editorRootRef.current;
            if (!editorRoot) return;
            const htmlRaw = e.clipboardData.getData('text/html');
            if (!htmlRaw.trim()) return;
            e.preventDefault();
            const plain = e.clipboardData.getData('text/plain');
            const cleaned = sanitizePastedRichTextHtml(htmlRaw);
            rememberSelectionInEditor();
            editorRoot.focus();
            const sel = window.getSelection();
            if (!sel) return;
            let range: Range | null = null;
            if (sel.rangeCount > 0) {
                const r = sel.getRangeAt(0);
                if (editorRoot.contains(r.commonAncestorContainer)) {
                    range = r;
                }
            }
            if (!range && lastRangeInEditorRef.current) {
                const backup = lastRangeInEditorRef.current;
                if (editorRoot.contains(backup.commonAncestorContainer)) {
                    range = backup.cloneRange();
                    try {
                        sel.removeAllRanges();
                        sel.addRange(range);
                    } catch {
                        range = null;
                    }
                }
            }
            if (!range) {
                range = document.createRange();
                range.selectNodeContents(editorRoot);
                range.collapse(false);
                try {
                    sel.removeAllRanges();
                    sel.addRange(range);
                } catch {
                    return;
                }
            }
            try {
                document.execCommand('styleWithCSS', false, 'true');
                if (cleaned.trim()) {
                    document.execCommand('insertHTML', false, cleaned);
                } else if (plain) {
                    document.execCommand('insertText', false, plain);
                }
            } catch {
                if (plain) {
                    document.execCommand('insertText', false, plain);
                }
            }
            pruneRedundantNestedParentFontSizeInRichTextRoot(editorRoot);
            const nextHtml = editorRoot.innerHTML;
            lastSyncedValueRef.current = nextHtml;
            onChange(nextHtml);
            rememberSelectionInEditor();
        },
        [onChange]
    );

    const handleEditorBeforeInput = (e: React.FormEvent<HTMLDivElement>) => {
        const be = e.nativeEvent as InputEvent;
        if (be.inputType !== 'insertText' && be.inputType !== 'insertCompositionText') return;
        if (be.data == null || be.data.length === 0) return;
        const root = editorRootRef.current;
        if (!root || document.activeElement !== root) return;
        
        const style: Partial<CSSStyleDeclaration> = {};

        // 颜色：优先使用用户选择的颜色
        const color = userSelectedColor || (customSwatchActive ? customPickerColor : null);
        if (color && parseCssColorToRgb(color)) {
            style.color = color;
        }

        // 字号：以工具栏选择为准（未选择则不干预）
        const activeFontSizePx = toolbarExplicitTypingFontSizePxRef.current;
        if (activeFontSizePx != null) {
            style.fontSize = `${activeFontSizePx}px`;
        }

        // 若工具栏没有强制样式，则交给浏览器默认输入
        if (Object.keys(style).length === 0) return;

        be.preventDefault();
        insertStyledTextAtSelection(be.data, style);
    };

    const wrapSelectionWithSpanStyle = (style: Partial<CSSStyleDeclaration>, rangeOverride?: Range | null): boolean => {
        const source = rangeOverride ?? getSelectionRange();
        if (!source || source.collapsed) return false;
        const range = source.cloneRange();

        const span = document.createElement('span');
        Object.entries(style).forEach(([k, v]) => {
            if (v == null) return;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (span.style as any)[k] = v;
        });

        try {
            span.appendChild(range.extractContents());
            range.insertNode(span);
        } catch {
            return false;
        }

        if (style.fontSize) {
            stripInlineFontSizeFromDescendants(span);
        }

        const root = editorRootRef.current;
        if (!root) return false;
        pruneRedundantNestedParentFontSizeInRichTextRoot(root);
        const html = root.innerHTML;
        lastSyncedValueRef.current = html;
        onChange(html);
        return true;
    };

    const applyFontSizeToSelection = (nextFontSizePx: number) => {
        const root = editorRootRef.current;
        if (!root) return;
        const fontSize = `${nextFontSizePx}px`;
        toolbarExplicitTypingFontSizePxRef.current = nextFontSizePx;

        const sel = window.getSelection();
        let range: Range | null = null;
        if (sel && sel.rangeCount > 0) {
            const r = sel.getRangeAt(0);
            if (root.contains(r.commonAncestorContainer)) {
                range = r;
            }
        }
        if (!range && lastRangeInEditorRef.current) {
            const backup = lastRangeInEditorRef.current;
            if (root.contains(backup.commonAncestorContainer)) {
                range = backup.cloneRange();
                try {
                    sel?.removeAllRanges();
                    sel?.addRange(range);
                } catch {
                    /* range 可能已失效 */
                }
            }
        }
        if (!range || !sel || sel.rangeCount === 0) {
            return;
        }

        const bookmark = range.cloneRange();
        const isCollapsed = bookmark.collapsed;

        root.focus();
        sel.removeAllRanges();
        try {
            sel.addRange(bookmark);
        } catch {
            return;
        }

        if (!isCollapsed) {
            const ok = wrapSelectionWithSpanStyle({ fontSize }, bookmark);
            if (ok) {
                setFontSizePx(null);
            }
            return;
        }

        try {
            const r = bookmark.cloneRange();
            const span = document.createElement('span');
            span.style.fontSize = fontSize;
            const zw = document.createTextNode('\u200b');
            span.appendChild(zw);
            r.insertNode(span);
            const caret = document.createRange();
            const len = zw.nodeValue?.length ?? 0;
            caret.setStart(zw, len);
            caret.collapse(true);
            sel.removeAllRanges();
            sel.addRange(caret);
        } catch {
            return;
        }

        pruneRedundantNestedParentFontSizeInRichTextRoot(root);
        const html = root.innerHTML;
        lastSyncedValueRef.current = html;
        onChange(html);
        setFontSizePx(null);
    };

    const applyColorToSelection = (color: string) => {
        const root = editorRootRef.current;
        if (!root) return;

        pendingAppliedColorRef.current = color;
        toolbarExplicitTypingColorRef.current = color;

        const sel = window.getSelection();
        let range: Range | null = null;
        if (sel && sel.rangeCount > 0) {
            const r = sel.getRangeAt(0);
            if (root.contains(r.commonAncestorContainer)) {
                range = r;
            }
        }
        if (!range && lastRangeInEditorRef.current) {
            const backup = lastRangeInEditorRef.current;
            if (root.contains(backup.commonAncestorContainer)) {
                range = backup.cloneRange();
                try {
                    sel?.removeAllRanges();
                    sel?.addRange(range);
                } catch {
                    /* range 可能已失效 */
                }
            }
        }
        if (!range || !sel || sel.rangeCount === 0) {
            scheduleRefreshActiveSwatch();
            return;
        }

        const bookmark = range.cloneRange();
        const isCollapsed = bookmark.collapsed;

        root.focus();
        sel.removeAllRanges();
        try {
            sel.addRange(bookmark);
        } catch {
            scheduleRefreshActiveSwatch();
            return;
        }

        if (isPresetWhiteColor(color)) {
            if (!isCollapsed) {
                const ok = wrapSelectionWithSpanStyle({
                    color: PRESET_WHITE_HEX,
                    fontSize: `${POD_OVERLAY_EDITOR_WHITE_TEXT_PX}px`
                });
                if (ok) {
                    pruneRedundantNestedParentFontSizeInRichTextRoot(root);
                    const html = root.innerHTML;
                    lastSyncedValueRef.current = html;
                    onChange(html);
                }
                scheduleRefreshActiveSwatch();
                return;
            }
            try {
                const r = bookmark.cloneRange();
                const span = document.createElement('span');
                span.style.color = PRESET_WHITE_HEX;
                span.style.fontSize = `${POD_OVERLAY_EDITOR_WHITE_TEXT_PX}px`;
                const zw = document.createTextNode('\u200b');
                span.appendChild(zw);
                r.insertNode(span);
                const caret = document.createRange();
                const len = zw.nodeValue?.length ?? 0;
                caret.setStart(zw, len);
                caret.collapse(true);
                sel.removeAllRanges();
                sel.addRange(caret);
                pruneRedundantNestedParentFontSizeInRichTextRoot(root);
                const html = root.innerHTML;
                lastSyncedValueRef.current = html;
                onChange(html);
            } catch {
                /* noop */
            }
            scheduleRefreshActiveSwatch();
            return;
        }

        let foreColorOk = false;
        try {
            document.execCommand('styleWithCSS', false, 'true');
            foreColorOk = document.execCommand('foreColor', false, color);
        } catch {
            foreColorOk = false;
        }

        if (foreColorOk) {
            pruneRedundantNestedParentFontSizeInRichTextRoot(root);
            const html = root.innerHTML;
            lastSyncedValueRef.current = html;
            onChange(html);
            scheduleRefreshActiveSwatch();
            return;
        }

        if (!isCollapsed) {
            sel.removeAllRanges();
            try {
                sel.addRange(bookmark.cloneRange());
            } catch {
                scheduleRefreshActiveSwatch();
                return;
            }
            wrapSelectionWithSpanStyle({ color });
            scheduleRefreshActiveSwatch();
            return;
        }

        try {
            const r = bookmark.cloneRange();
            const span = document.createElement('span');
            span.style.color = color;
            const zw = document.createTextNode('\u200b');
            span.appendChild(zw);
            r.insertNode(span);
            const caret = document.createRange();
            const len = zw.nodeValue?.length ?? 0;
            caret.setStart(zw, len);
            caret.collapse(true);
            sel.removeAllRanges();
            sel.addRange(caret);
            pruneRedundantNestedParentFontSizeInRichTextRoot(root);
            const html = root.innerHTML;
            lastSyncedValueRef.current = html;
            onChange(html);
            scheduleRefreshActiveSwatch();
        } catch {
            scheduleRefreshActiveSwatch();
        }
    };

    /** 对齐选项下拉菜单（包含左、中、右对齐） */
    const alignMenuItems = [
        { key: 'left', label: <AlignLeftOutlined /> },
        { key: 'center', label: <AlignCenterOutlined /> },
        { key: 'right', label: <AlignRightOutlined /> }
    ];

    const applyTextAlignment = (alignment: string) => {
        const root = editorRootRef.current;
        if (!root) return;
        
        // 确保编辑器获得焦点
        root.focus();
        
        // 应用对齐样式
        const cmd = `justify${alignment.charAt(0).toUpperCase()}${alignment.slice(1)}`;
        document.execCommand(cmd, false);
        
        // 对于可能的选区问题，使用另一种方法确保对齐应用
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            let blockElement: HTMLElement | null = null;
            
            // 检查commonAncestorContainer是否为Element类型
            if (range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE) {
                blockElement = (range.commonAncestorContainer as Element).closest('p, div, span');
            }
            
            if (blockElement) {
                blockElement.style.textAlign = alignment;
            } else {
                // 如果没有块级元素，创建一个
                const p = document.createElement('p');
                p.style.textAlign = alignment;
                
                // 移动内容到新的p元素
                while (root.firstChild) {
                    p.appendChild(root.firstChild);
                }
                
                root.appendChild(p);
                
                // 重新设置选区
                const newRange = document.createRange();
                newRange.selectNodeContents(p);
                newRange.collapse(false);
                selection.removeAllRanges();
                selection.addRange(newRange);
            }
        } else {
            // 如果没有选区，直接设置根元素的对齐
            root.style.textAlign = alignment;
        }
        
        // 更新状态
        const html = root.innerHTML;
        lastSyncedValueRef.current = html;
        onChange(html);
    };

    // 清理多余的嵌套p标签
    const cleanUpNestedParagraphs = (element: HTMLElement) => {
        // 递归清理嵌套的p标签
        const cleanRecursive = (parent: Node) => {
            const children = Array.from(parent.childNodes);
            children.forEach(child => {
                if (child.nodeType === Node.ELEMENT_NODE && (child as HTMLElement).tagName === 'P') {
                    const pElement = child as HTMLElement;
                    // 递归处理子元素
                    cleanRecursive(pElement);
                    
                    // 将p元素的内容移动到父元素
                    while (pElement.firstChild) {
                        parent.insertBefore(pElement.firstChild, pElement);
                    }
                    // 删除空的p元素
                    pElement.remove();
                } else if (child.nodeType === Node.ELEMENT_NODE) {
                    // 递归处理其他元素
                    cleanRecursive(child);
                }
            });
        };
        
        // 开始清理
        cleanRecursive(element);
        
        // 确保至少有一个p元素
        if (element.children.length === 0) {
            const p = document.createElement('p');
            element.appendChild(p);
        }
    };

    useEffect(() => {
        const root = editorRootRef.current;
        if (!root) return;
        const isFocused = document.activeElement === root;
        const next = sanitizePodRichHtml(value || '');
        if (isFocused) return;
        if (lastSyncedValueRef.current === next) return;
        if (root.innerHTML === next) {
            lastSyncedValueRef.current = next;
            return;
        }
        root.innerHTML = next;
        lastSyncedValueRef.current = next;
    }, [value]);

    const fontSizeSelectValue: string =
        fontSizePx === null || fontSizePx === undefined
            ? FONT_SIZE_SELECT_PLACEHOLDER
            : String(fontSizePx);

    return (
        <div className="rounded-md border border-gray-200">
            <div className="flex items-center gap-1 border-b border-gray-100 px-2 py-1 bg-gray-50">
                <Tooltip title="加粗">
                    <Button type="text" size="small" icon={<BoldOutlined />} onClick={() => applyCommand('bold')} />
                </Tooltip>
                {/* 斜体按钮暂时隐藏（后续可能需要） */}
                {false && (
                    <Tooltip title="斜体">
                        <Button type="text" size="small" icon={<ItalicOutlined />} onClick={() => applyCommand('italic')} />
                    </Tooltip>
                )}
                
                <div className="inline-flex items-center gap-0.5">
                    <Dropdown
                        trigger={['click']}
                        menu={{
                            items: alignMenuItems.map((a) => ({
                                key: a.key,
                                label: a.label
                            })),
                            onClick: ({ key }) => {
                                applyTextAlignment(String(key));
                            }
                        }}
                    >
                        <span
                            className="inline-flex items-center"
                            onMouseDown={() => {
                                rememberSelectionInEditor();
                            }}
                        >
                            <Tooltip title="对齐方式">
                                <div className="h-7 w-[44px] rounded border border-gray-200 bg-white px-1 text-[12px] text-gray-800 flex items-center justify-center cursor-pointer hover:border-gray-300">
                                    <AlignCenterOutlined className="mr-1" />
                                    <DownOutlined className="text-[10px]" />
                                </div>
                            </Tooltip>
                        </span>
                    </Dropdown>
                </div>

                <Tooltip title="字号：选中后设置。POD 约定橘16 / 白字22；白字中的数字可另选 24">
                    <label className="inline-flex items-center gap-1">
                        <select
                            className="h-7 min-w-[3.5rem] rounded border border-gray-200 bg-white px-1 text-[12px] text-gray-800"
                            value={fontSizeSelectValue}
                            onMouseDown={() => {
                                // 记录当前选区，避免点击工具栏导致选区丢失
                                rememberSelectionInEditor();
                            }}
                            onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === FONT_SIZE_SELECT_PLACEHOLDER) {
                                    setFontSizePx(null);
                                    toolbarExplicitTypingFontSizePxRef.current = null;
                                    return;
                                }
                                const v = Number(raw);
                                if (Number.isNaN(v)) {
                                    return;
                                }
                                applyFontSizeToSelection(v);
                            }}
                        >
                            <option value={FONT_SIZE_SELECT_PLACEHOLDER}>字号</option>
                            {RICH_TEXT_FONT_SIZE_OPTIONS_PX.map((s) => (
                                <option key={s} value={String(s)}>
                                    {s}
                                </option>
                            ))}
                        </select>
                    </label>
                </Tooltip>



                <div className="flex flex-wrap items-center gap-1.5 pl-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1">
                        {RICH_TEXT_COLOR_PRESETS.map((c) => (
                            <Tooltip
                                key={c.value}
                                title={`${c.label}：选中文字后点此可改色，或先点选颜色再输入文字`}
                            >
                                <button
                                    type="button"
                                    className={`h-6 w-6 shrink-0 cursor-pointer rounded border transition-all ${
                                        userSelectedColor === c.value
                                            ? colorsClose(c.value, '#ffffff', 6)
                                                ? 'border-2 border-gray-600 ring-2 ring-gray-600 ring-offset-2 bg-gray-100'
                                                : 'border-2 border-blue-600 ring-2 ring-blue-600 ring-offset-2 bg-blue-50'
                                            : 'border border-gray-300 hover:border-blue-400 hover:ring-1 hover:ring-blue-300'
                                    }`}
                                    style={{ backgroundColor: c.value }}
                                    aria-label={`颜色 ${c.label}`}
                                    aria-pressed={userSelectedColor === c.value}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        toolbarExplicitTypingColorRef.current = c.value;
                                        if (isPresetWhiteColor(c.value)) {
                                            toolbarExplicitTypingFontSizePxRef.current =
                                                POD_OVERLAY_EDITOR_WHITE_TEXT_PX;
                                        } else if (isPresetOrangeColor(c.value)) {
                                            toolbarExplicitTypingFontSizePxRef.current =
                                                POD_OVERLAY_EDITOR_ORANGE_PX;
                                        } else {
                                            toolbarExplicitTypingFontSizePxRef.current = null;
                                        }
                                        setUserSelectedColor(c.value);
                                        setCustomSwatchActive(false);
                                    }}
                                    onClick={() => {
                                        applyColorToSelection(c.value);
                                    }}
                                />
                            </Tooltip>
                        ))}
                        <Tooltip title="自定义颜色：选中文字后点此可改色，或先点选颜色再输入文字（打开调色板会暂失焦）">
                            <label
                                className={`relative inline-flex h-6 w-6 shrink-0 cursor-pointer overflow-hidden rounded border transition-all ${
                                    customSwatchActive
                                        ? 'border-2 border-blue-600 ring-2 ring-blue-600 ring-offset-2 bg-blue-50'
                                        : 'border border-gray-300 hover:border-blue-400 hover:ring-1 hover:ring-blue-300'
                                }`}
                                onMouseDown={(e) => e.preventDefault()}
                            >
                                <span
                                    className="pointer-events-none absolute inset-0"
                                    style={{
                                        background:
                                            'linear-gradient(135deg, #f5222d 0%, #fa8c16 14%, #fadb14 28%, #52c41a 42%, #13c2c2 56%, #1677ff 70%, #722ed1 84%, #eb2f96 100%)'
                                    }}
                                    aria-hidden
                                />
                                <input
                                    aria-label="自定义颜色"
                                    type="color"
                                    value={customPickerColor}
                                    onChange={(e) => {
                                        const next = e.target.value;
                                        toolbarExplicitTypingColorRef.current = next;
                                        toolbarExplicitTypingFontSizePxRef.current = null;
                                        setCustomPickerColor(next);
                                        setUserSelectedColor(null);
                                        setCustomSwatchActive(true);
                                        const rootEl = editorRootRef.current;
                                        const backup = lastRangeInEditorRef.current;
                                        if (rootEl && backup) {
                                            try {
                                                if (rootEl.contains(backup.commonAncestorContainer)) {
                                                    const s = window.getSelection();
                                                    if (s) {
                                                        s.removeAllRanges();
                                                        s.addRange(backup.cloneRange());
                                                    }
                                                    rootEl.focus();
                                                }
                                            } catch {
                                                /* range 可能已失效 */
                                            }
                                        }
                                        applyColorToSelection(next);
                                    }}
                                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                />
                            </label>
                        </Tooltip>
                    </div>
                </div>
            </div>
            <div
                contentEditable
                suppressContentEditableWarning
                className={`min-h-[64px] px-3 py-2 outline-none rounded-b-md [&_p]:m-0 ${
                    surface === 'light' ? 'bg-white text-black' : 'bg-black/55 text-white'
                }`}
                style={{ fontSize: `${POD_OVERLAY_EDITOR_ORANGE_PX}px` }}
                ref={editorRootRef}
                onBeforeInput={handleEditorBeforeInput}
                onPaste={handleEditorPaste}
                onInput={(e) => {
                    const html = (e.target as HTMLDivElement).innerHTML;
                    lastSyncedValueRef.current = html;
                    onChange(html);
                }}
                onMouseUp={() => {
                    rememberSelectionInEditor();
                }}
                onKeyUp={() => {
                    rememberSelectionInEditor();
                }}
                onFocus={() => {
                    rememberSelectionInEditor();
                    onFocus?.();
                }}
                onClick={() => {
                    // 点击输入区域时，保持颜色选择状态
                }}
                onBlur={() => {
                    const root = editorRootRef.current;
                    if (!root) return;
                    pruneRedundantNestedParentFontSizeInRichTextRoot(root);
                    const normalized = injectDefaultFontSizeIntoRichTextHtml(
                        root.innerHTML,
                        POD_OVERLAY_EDITOR_ORANGE_PX
                    );
                    if (normalized !== lastSyncedValueRef.current) {
                        lastSyncedValueRef.current = normalized;
                        if (root.innerHTML !== normalized) {
                            root.innerHTML = normalized;
                        }
                        onChange(normalized);
                    }
                }}
                data-placeholder={placeholder || '请输入内容'}
            />
        </div>
    );
};

export default RichTextColorEditor;